import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from '../../firebase';
import { authFetch } from '../utils/cloudFunctionsAuth';
import {
  DRAFT_ATTACHMENTS_MAX_BYTES,
  formatBytes,
  guardDraftAttachments,
  utf8Length,
} from '../pages/mail/utils/attachmentLimits';

// The Graph-draft callables 403 at the edge (→ CORS errors), so we call their
// HTTP twins in graph-callable-compat.ts instead.
const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

/** POST JSON to a Cloud Function HTTP endpoint with the user's ID token. */
async function callFn<T = any>(name: string, body: Record<string, any>): Promise<T> {
  const resp = await authFetch(`${CF_BASE}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await resp.text();
  let data: any = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
  if (!resp.ok || data?.ok === false) {
    throw new Error(data?.error || `HTTP ${resp.status}`);
  }
  return data as T;
}

// -- Draft document size ------------------------------------------------------
//
// One email_drafts doc may not exceed Firestore's 1 MiB (1,048,576 byte) cap.
// The body (a pasted screenshot is a data: URL INSIDE htmlBody, and a forward
// re-embeds its cid: images the same way) and the inline attachments share that
// budget, so the attachment allowance must shrink as the body grows - a fixed
// 700 KB attachment budget plus a 600 KB body is already over the cap.
//
// Measured like Firestore does (UTF-8 bytes, not UTF-16 units or JSON length:
// JSON escapes every quote in the HTML, which would reject drafts that fit).
/** Ceiling for the whole doc. ~48 KB under the hard cap, so a slightly-off estimate still fits. */
const DRAFT_DOC_MAX_BYTES = 1_000_000;
/** Field names, timestamps, ids, doc path: ~1 KB in reality, reserved generously. */
const DRAFT_DOC_OVERHEAD_BYTES = 8 * 1024;

/** `code` on the error thrown when a draft cannot fit one Firestore doc, so a composer can tell it from a network failure. */
export const DRAFT_TOO_LARGE_CODE = 'draft-too-large';

const docStringBytes = (s: unknown): number => (typeof s === 'string' ? utf8Length(s) + 1 : 0);
const docStringsBytes = (list: unknown): number =>
  Array.isArray(list) ? list.reduce((n: number, v: unknown) => n + docStringBytes(v), 0) : 0;

interface DraftDocParts {
  htmlBody: string;
  textBody: string;
  subject: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  attachments: unknown;
  // Small, but unbounded strings (a filename is whatever the sender called it),
  // so they are counted like the other text fields instead of assumed away.
  forwardExcludeNames?: string[];
  forwardFromGraphId?: string;
  linkedInvoiceId?: string;
}

/**
 * Make a draft fit ONE Firestore document, BEFORE any side effect.
 *
 * Attachments get whatever the body leaves over (never more than the normal
 * 700 KB): oversize inline bytes are dropped, metadata kept, and `trimmed` is
 * reported - the chip comes back as an error chip that blocks Send until the
 * file is re-attached, so nothing ever goes out silently missing a file.
 *
 * The BODY is never altered. If it alone (even with every attachment reduced to
 * metadata) cannot fit, this throws DRAFT_TOO_LARGE_CODE instead. Stripping the
 * data: images from a stored copy would be wrong: the composer's live
 * onSnapshot sees a body that differs from the editor's and re-applies it once
 * idle, so the owner would send a reply without the image they pasted. Failing
 * here - before the Outlook draft is created - also stops every retry of the
 * autosave from leaving another multi-MB orphan draft in Outlook.
 */
function fitDraftToDocLimit(parts: DraftDocParts): { attachments: any[]; trimmed: boolean } {
  const fixedBytes =
    DRAFT_DOC_OVERHEAD_BYTES +
    docStringBytes(parts.htmlBody) +
    docStringBytes(parts.textBody) +
    docStringBytes(parts.subject) +
    docStringsBytes(parts.to) +
    docStringsBytes(parts.cc) +
    docStringsBytes(parts.bcc) +
    docStringsBytes(parts.forwardExcludeNames) +
    docStringBytes(parts.forwardFromGraphId) +
    docStringBytes(parts.linkedInvoiceId);
  const budget = Math.max(0, Math.min(DRAFT_ATTACHMENTS_MAX_BYTES, DRAFT_DOC_MAX_BYTES - fixedBytes));
  const guarded = guardDraftAttachments(parts.attachments, budget);
  // After the guard only refs / metadata / what fits remain, so this is cheap.
  const attachmentBytes = guarded.attachments.length > 0 ? utf8Length(JSON.stringify(guarded.attachments)) : 0;
  const total = fixedBytes + attachmentBytes;
  if (total > DRAFT_DOC_MAX_BYTES) {
    const err: any = new Error(
      `draft is ${formatBytes(total)} - over the 1 MB save limit. Pasted images count; attach large images as files instead`,
    );
    err.code = DRAFT_TOO_LARGE_CODE;
    throw err;
  }
  return guarded;
}

// -- Forward bookkeeping ------------------------------------------------------

/** Most excluded original-attachment names one draft remembers (matches what the composer sends). */
const FORWARD_EXCLUDE_NAMES_MAX = 200;

/**
 * The owner's "left out of this forward" list, cleaned for storage / hydration:
 * strings only, no blanks, capped. Returns undefined ONLY when the input is not an
 * array (field absent) - an EMPTY array is a real value and must survive, or a
 * [restore] click would be forgotten on the next autosave and the removed file
 * would come back into a resumed forward.
 */
function cleanForwardExcludeNames(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value
    .filter((n: unknown): n is string => typeof n === 'string' && n !== '')
    .slice(0, FORWARD_EXCLUDE_NAMES_MAX);
}

/**
 * Attachments for a DUPLICATED draft. Mirrors attachmentsForDuplicate in
 * pages/mail/components/DraftsPanel.tsx (kept separate on purpose: importing a
 * component module here would make a runtime cycle through EmailPanelContext,
 * which imports this service). Keep the two rules identical.
 *
 * A Storage ref (storagePath) is NEVER shared between two drafts: objects are not
 * reference-counted, so sending or discarding either draft would delete the file
 * the other still points at. A ref is reduced to metadata only - the composer
 * hydrates that as an error chip that blocks Send until the file is attached
 * again, so nothing goes out silently missing a file. Inline base64 items are
 * independent copies and stay.
 */
function attachmentsForDuplicate(list: unknown): any[] {
  if (!Array.isArray(list)) return [];
  const out: any[] = [];
  list.forEach((raw) => {
    if (!raw || typeof raw !== 'object') return;
    // `rest` is a fresh object, so the copy never aliases the source item.
    const { storagePath, ...rest } = raw as any;
    if (typeof storagePath === 'string' && storagePath && !rest.content) {
      const meta: any = {};
      if (typeof rest.filename === 'string' && rest.filename) meta.filename = rest.filename;
      if (typeof rest.contentType === 'string' && rest.contentType) meta.contentType = rest.contentType;
      if (typeof rest.size === 'number' && rest.size > 0) meta.size = rest.size;
      out.push(meta);
      return;
    }
    out.push(rest);
  });
  return out;
}

// Define the email draft interface
export interface EmailDraft {
  id?: string;                   // Firestore document ID (optional for new drafts)
  userId: string;               // User who created the draft
  subject: string;              // Email subject
  htmlBody: string;             // HTML content
  textBody: string;             // Plain text fallback
  to: string[];                 // Recipients
  cc?: string[];                // CC recipients
  bcc?: string[];               // BCC recipients
  attachments?: any[];          // File attachments (using existing AttachmentData structure)
  senderAccountId: string;      // Selected sender account
  replyToEmailId?: string;      // Original email ID (for replies/forwards)
  isReply?: boolean;            // Whether this is a reply
  isForward?: boolean;          // Whether this is a forward
  createdAt: Timestamp;         // Creation timestamp
  updatedAt: Timestamp;         // Last update timestamp
  status: 'draft' | 'sending' | 'sent'; // Draft status
  lastSavedAt?: Timestamp;      // Last auto-save timestamp
  wordCount?: number;           // Body word count for sorting
  // Graph sync fields (optional)
  source?: 'portal' | 'graph' | 'recurring-invoice-auto';  // Where this draft originated
  graphMailbox?: string;        // Mailbox used in Graph
  graphMessageId?: string;      // Graph message id for the draft
  // LEGACY spelling of the two above: Outlook-synced drafts written before the
  // sync stamped graphMailbox/graphMessageId carry only these.
  mailbox?: string;
  graphId?: string;
  // True when the save had to drop the bytes of an oversize inline attachment
  // (Firestore's 1 MiB doc cap); the attachment's metadata is kept.
  attachmentsTrimmed?: boolean;
  // When set, sending this draft marks the linked invoice SENT (used by the
  // auto-drafted recurring-invoice emails).
  linkedInvoiceId?: string;
  // Forward drafts: the original attachments (by filename) the owner removed.
  // An EMPTY array is meaningful (everything restored) and is stored as such.
  forwardExcludeNames?: string[];
  // Forward drafts: Graph id of the message being forwarded.
  forwardFromGraphId?: string;
}

// Utility function to calculate word count
const calculateWordCount = (text: string): number => {
  if (!text) return 0;
  // Remove HTML tags and count words
  const cleanText = text.replace(/<[^>]*>/g, '').trim();
  return cleanText.split(/\s+/).filter(word => word.length > 0).length;
};

// Utility function to generate plain text from HTML
const generateTextFromHtml = (html: string): string => {
  if (!html) return '';
  // Basic HTML to text conversion - remove tags and clean up
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

// Convert Firestore document to EmailDraft
const convertDocToEmailDraft = (doc: QueryDocumentSnapshot<DocumentData>): EmailDraft => {
  const data = doc.data();
  return {
    id: doc.id,
    userId: data.userId,
    subject: data.subject || '',
    htmlBody: data.htmlBody || '',
    textBody: data.textBody || '',
    to: Array.isArray(data.to) ? data.to : [],
    cc: Array.isArray(data.cc) ? data.cc : [],
    bcc: Array.isArray(data.bcc) ? data.bcc : [],
    attachments: data.attachments || [],
    senderAccountId: data.senderAccountId || '',
    replyToEmailId: data.replyToEmailId,
    isReply: data.isReply || false,
    isForward: data.isForward || false,
    linkedInvoiceId: data.linkedInvoiceId || undefined,
    // Without these a resumed forward re-includes attachments the owner removed.
    forwardExcludeNames: cleanForwardExcludeNames(data.forwardExcludeNames),
    forwardFromGraphId:
      typeof data.forwardFromGraphId === 'string' && data.forwardFromGraphId ? data.forwardFromGraphId : undefined,
    createdAt: data.createdAt || Timestamp.now(),
    updatedAt: data.updatedAt || Timestamp.now(),
    status: data.status || 'draft',
    lastSavedAt: data.lastSavedAt,
    wordCount: data.wordCount || 0,
    source: data.source,
    graphMailbox: data.graphMailbox,
    graphMessageId: data.graphMessageId,
    mailbox: data.mailbox,
    graphId: data.graphId,
    attachmentsTrimmed: data.attachmentsTrimmed || undefined,
  };
};

// An Outlook draft is addressed by (mailbox, message id). Synced drafts carry
// graphMailbox/graphMessageId now, but older docs only have mailbox/graphId -
// reading just the new pair made editing such a draft create a DUPLICATE Outlook
// draft and deleting it leave the Outlook copy behind.
const graphMailboxOf = (d?: Partial<EmailDraft> | null): string => String(d?.graphMailbox || d?.mailbox || '');
const graphMessageIdOf = (d?: Partial<EmailDraft> | null): string => String(d?.graphMessageId || d?.graphId || '');

class DraftService {
  private draftsCollection = 'email_drafts';

  private async ensureGraphDraft(draftData: Partial<EmailDraft>): Promise<{ mailbox: string; id: string } | null> {
    const mailbox = typeof draftData.senderAccountId === 'string' ? draftData.senderAccountId : '';
    if (!mailbox || !mailbox.includes('@')) return null;

    const data = await callFn('createGraphDraftHttp', {
      mailbox,
      subject: draftData.subject || '',
      html: draftData.htmlBody || '',
      text: draftData.textBody || '',
      to: draftData.to || [],
      cc: draftData.cc || [],
      bcc: draftData.bcc || [],
    });
    if (!data?.ok || !data?.id || !data?.mailbox) return null;
    return { mailbox: String(data.mailbox), id: String(data.id) };
  }

  private async updateGraphDraft(mailbox: string, id: string, updates: Partial<EmailDraft>): Promise<void> {
    await callFn('updateGraphDraftHttp', {
      mailbox,
      id,
      subject: updates.subject,
      html: updates.htmlBody,
      text: updates.textBody,
      to: updates.to,
      cc: updates.cc,
      bcc: updates.bcc,
    });
  }

  private async deleteGraphDraft(mailbox: string, id: string): Promise<void> {
    await callFn('deleteGraphDraftHttp', { mailbox, id });
  }

  /** Best-effort removal of an Outlook draft THIS call just created whose Firestore write then failed. */
  private async discardUnreferencedGraphDraft(graph: { mailbox: string; id: string }): Promise<void> {
    try {
      await this.deleteGraphDraft(graph.mailbox, graph.id);
    } catch (e) {
      console.warn('[draftService] Could not remove the orphaned Outlook draft after a failed save', e);
    }
  }

  async syncGraphDraftsForCurrentUser(limit: number = 100): Promise<void> {
    // The endpoint reads { mailbox?, limit } — pass `limit` so the cap is honoured.
    await callFn('syncGraphDraftsToFirestoreHttp', { limit });
  }

  /**
   * Save a new email draft
   */
  async saveDraft(draftData: Partial<EmailDraft>, userId: string): Promise<string> {
    try {
      const now = Timestamp.now();
      const wordCount = draftData.htmlBody ? calculateWordCount(draftData.htmlBody) : 0;
      const textBody = draftData.htmlBody ? generateTextFromHtml(draftData.htmlBody) : (draftData.textBody || '');

      // Fit the doc under Firestore's 1 MiB cap FIRST, before any side effect:
      // oversize inline attachment bytes are dropped (metadata kept) instead of
      // failing the autosave, and a body that is too big on its own throws here -
      // not after createGraphDraftHttp has already left an Outlook draft behind.
      const forwardExcludeNames = cleanForwardExcludeNames(draftData.forwardExcludeNames);
      const guarded = fitDraftToDocLimit({
        htmlBody: draftData.htmlBody || '',
        textBody,
        subject: draftData.subject || '',
        to: draftData.to,
        cc: draftData.cc,
        bcc: draftData.bcc,
        attachments: draftData.attachments,
        forwardExcludeNames,
        forwardFromGraphId: draftData.forwardFromGraphId,
        linkedInvoiceId: draftData.linkedInvoiceId,
      });
      if (guarded.trimmed) {
        console.warn('[draftService] attachment bytes too large for a draft doc - saved metadata only');
      }

      // Create (or mirror) a Graph draft up-front so we can store graph ids for bi-directional sync.
      // If Graph draft creation fails, we still save locally so work is not lost.
      let graph: { mailbox: string; id: string } | null = null;
      try {
        graph = await this.ensureGraphDraft({
          ...draftData,
          textBody,
        });
      } catch (e) {
        console.warn('[draftService] Graph draft create failed; saving locally only', e);
      }

      const draftToSave: any = {
        userId,
        subject: draftData.subject || '',
        htmlBody: draftData.htmlBody || '',
        textBody,
        to: draftData.to || [],
        cc: draftData.cc || [],
        bcc: draftData.bcc || [],
        attachments: guarded.attachments,
        senderAccountId: draftData.senderAccountId || '',
        isReply: draftData.isReply || false,
        isForward: draftData.isForward || false,
        createdAt: now,
        updatedAt: now,
        status: 'draft' as const,
        lastSavedAt: now,
        wordCount,
        source: draftData.source || 'portal',
      };

      // Only include optional fields if defined (Firestore rejects undefined values)
      if (draftData.replyToEmailId !== undefined) {
        draftToSave.replyToEmailId = draftData.replyToEmailId;
      }
      // An EMPTY exclusion list is written on purpose (see cleanForwardExcludeNames).
      if (forwardExcludeNames !== undefined) draftToSave.forwardExcludeNames = forwardExcludeNames;
      if (typeof draftData.forwardFromGraphId === 'string' && draftData.forwardFromGraphId) {
        draftToSave.forwardFromGraphId = draftData.forwardFromGraphId;
      }
      if (typeof draftData.linkedInvoiceId === 'string' && draftData.linkedInvoiceId) {
        draftToSave.linkedInvoiceId = draftData.linkedInvoiceId;
      }
      if (guarded.trimmed) draftToSave.attachmentsTrimmed = true;
      if (graph) {
        draftToSave.graphMailbox = graph.mailbox;
        draftToSave.graphMessageId = graph.id;
      }

      let docRef;
      try {
        docRef = await addDoc(collection(db, this.draftsCollection), draftToSave);
      } catch (writeError) {
        // No doc exists, so nothing references the Outlook draft created above
        // and the caller (no id yet) retries with a fresh one every autosave:
        // remove it or Outlook Drafts fills with copies.
        if (graph) await this.discardUnreferencedGraphDraft(graph);
        throw writeError;
      }
      return docRef.id;
    } catch (error) {
      console.error('Error saving draft:', error);
      // Keep the specific "too large" reason (the composer can act on it).
      if ((error as any)?.code === DRAFT_TOO_LARGE_CODE) throw error;
      throw new Error('Failed to save email draft');
    }
  }

  /**
   * Update an existing email draft
   */
  async updateDraft(draftId: string, updates: Partial<EmailDraft>): Promise<void> {
    try {
      const now = Timestamp.now();
      const updateData: any = {
        updatedAt: now,
        lastSavedAt: now,
      };

      // Load existing draft to retrieve graph linkage, if any.
      const existing = await this.getDraftById(draftId);
      const existingGraphMailbox = graphMailboxOf(existing);
      const existingGraphId = graphMessageIdOf(existing);
      const graphMailbox = updates.senderAccountId || existingGraphMailbox || existing?.senderAccountId || '';
      const graphId = existingGraphId || null;

      // Size gate BEFORE any Graph call, on the doc as it will be AFTER this update
      // (an update that changes only the body still has to share the 1 MiB doc with
      // the attachments already stored on it, and vice versa). Only the fields that
      // can grow the doc trigger it, so marking a draft sent never depends on it.
      // Throwing here matters: a doc with no Outlook draft yet would otherwise get a
      // fresh one created on every retry of a write that can never succeed.
      const forwardExcludeNames = cleanForwardExcludeNames(updates.forwardExcludeNames);
      let fitted: { attachments: any[]; trimmed: boolean } | null = null;
      if (updates.htmlBody !== undefined || updates.attachments !== undefined) {
        fitted = fitDraftToDocLimit({
          htmlBody: updates.htmlBody !== undefined ? updates.htmlBody : (existing?.htmlBody || ''),
          textBody: updates.htmlBody !== undefined ? generateTextFromHtml(updates.htmlBody) : (existing?.textBody || ''),
          subject: updates.subject !== undefined ? updates.subject : (existing?.subject || ''),
          to: updates.to !== undefined ? updates.to : existing?.to,
          cc: updates.cc !== undefined ? updates.cc : existing?.cc,
          bcc: updates.bcc !== undefined ? updates.bcc : existing?.bcc,
          attachments: updates.attachments !== undefined ? updates.attachments : existing?.attachments,
          forwardExcludeNames: forwardExcludeNames !== undefined ? forwardExcludeNames : existing?.forwardExcludeNames,
          forwardFromGraphId: updates.forwardFromGraphId !== undefined ? updates.forwardFromGraphId : existing?.forwardFromGraphId,
          linkedInvoiceId: updates.linkedInvoiceId !== undefined ? updates.linkedInvoiceId : existing?.linkedInvoiceId,
        });
      }
      // Set only when THIS call creates an Outlook draft (see the write below).
      let createdGraph: { mailbox: string; id: string } | null = null;

      // If marking as sent, delete the Graph draft (so it disappears from Outlook Drafts).
      if (updates.status === 'sent' && existingGraphMailbox && existingGraphId) {
        try {
          await this.deleteGraphDraft(existingGraphMailbox, existingGraphId);
        } catch (e) {
          console.warn('[draftService] Failed to delete Graph draft on sent', e);
        }
      } else {
        // For regular edits, ensure a Graph draft exists, then patch it.
        let ensuredGraphId = graphId;
        let ensuredGraphMailbox = existingGraphMailbox || (typeof graphMailbox === 'string' ? graphMailbox : '');
        if (!ensuredGraphId) {
          try {
            const created = await this.ensureGraphDraft({
              ...existing,
              ...updates,
              senderAccountId: graphMailbox as any,
              textBody: updates.htmlBody ? generateTextFromHtml(updates.htmlBody) : (updates.textBody || existing?.textBody || ''),
            });
            if (created) {
              createdGraph = created;
              ensuredGraphId = created.id;
              ensuredGraphMailbox = created.mailbox;
              updateData.graphMailbox = created.mailbox;
              updateData.graphMessageId = created.id;
              updateData.source = existing?.source || 'portal';
            }
          } catch (e) {
            console.warn('[draftService] Graph draft ensure failed; continuing local-only', e);
          }
        }

        if (ensuredGraphId && ensuredGraphMailbox) {
          try {
            await this.updateGraphDraft(String(ensuredGraphMailbox), String(ensuredGraphId), {
              ...existing,
              ...updates,
              senderAccountId: ensuredGraphMailbox as any,
            });
          } catch (e) {
            console.warn('[draftService] Graph draft update failed; continuing local-only', e);
          }
        }
      }

      // Update subject if provided
      if (updates.subject !== undefined) {
        updateData.subject = updates.subject;
      }

      // Update body content and recalculate word count
      if (updates.htmlBody !== undefined) {
        updateData.htmlBody = updates.htmlBody;
        updateData.textBody = generateTextFromHtml(updates.htmlBody);
        updateData.wordCount = calculateWordCount(updates.htmlBody);
      }

      // Update other fields if provided
      if (updates.to !== undefined) updateData.to = updates.to;
      if (updates.cc !== undefined) updateData.cc = updates.cc;
      if (updates.bcc !== undefined) updateData.bcc = updates.bcc;
      if (fitted) {
        // Same guard as saveDraft: never fail the autosave over attachment size.
        if (fitted.trimmed) {
          console.warn('[draftService] attachment bytes too large for a draft doc - saved metadata only');
        }
        if (updates.attachments !== undefined) {
          updateData.attachments = fitted.attachments;
          updateData.attachmentsTrimmed = fitted.trimmed;
        } else if (fitted.trimmed) {
          // A new body left no room for the inline bytes already on the doc:
          // persist them as metadata rather than fail the save.
          updateData.attachments = fitted.attachments;
          updateData.attachmentsTrimmed = true;
        }
      }
      if (updates.senderAccountId !== undefined) updateData.senderAccountId = updates.senderAccountId;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.replyToEmailId !== undefined) updateData.replyToEmailId = updates.replyToEmailId;
      if (updates.isReply !== undefined) updateData.isReply = updates.isReply;
      if (updates.isForward !== undefined) updateData.isForward = updates.isForward;
      // An EMPTY exclusion list is written on purpose (see cleanForwardExcludeNames).
      if (forwardExcludeNames !== undefined) updateData.forwardExcludeNames = forwardExcludeNames;
      if (typeof updates.forwardFromGraphId === 'string' && updates.forwardFromGraphId) {
        updateData.forwardFromGraphId = updates.forwardFromGraphId;
      }
      // Any string, '' included: an update may UNLINK the invoice (it hydrates back
      // to undefined). Never undefined - Firestore rejects it.
      if (typeof updates.linkedInvoiceId === 'string') updateData.linkedInvoiceId = updates.linkedInvoiceId;

      const draftRef = doc(db, this.draftsCollection, draftId);
      try {
        await updateDoc(draftRef, updateData);
      } catch (writeError) {
        // The new Outlook draft's ids were going to be stored by this write; it
        // failed, so nothing references that draft and the next retry would
        // create another. (A pre-existing Outlook draft is never touched.)
        if (createdGraph) await this.discardUnreferencedGraphDraft(createdGraph);
        throw writeError;
      }
    } catch (error) {
      console.error('Error updating draft:', error);
      // Keep the specific "too large" reason (the composer can act on it).
      if ((error as any)?.code === DRAFT_TOO_LARGE_CODE) throw error;
      throw new Error('Failed to update email draft');
    }
  }

  /**
   * Delete an email draft
   */
  async deleteDraft(draftId: string): Promise<void> {
    try {
      const existing = await this.getDraftById(draftId);
      const gMailbox = graphMailboxOf(existing);
      const gMessageId = graphMessageIdOf(existing);
      if (gMailbox && gMessageId) {
        try {
          await this.deleteGraphDraft(gMailbox, gMessageId);
        } catch (e) {
          console.warn('[draftService] Failed to delete Graph draft; continuing Firestore delete', e);
        }
      }
      const draftRef = doc(db, this.draftsCollection, draftId);
      await deleteDoc(draftRef);
    } catch (error) {
      console.error('Error deleting draft:', error);
      throw new Error('Failed to delete email draft');
    }
  }

  /**
   * Get a specific draft by ID
   */
  async getDraftById(draftId: string): Promise<EmailDraft | null> {
    try {
      const draftRef = doc(db, this.draftsCollection, draftId);
      const draftSnap = await getDoc(draftRef);

      if (draftSnap.exists()) {
        return convertDocToEmailDraft(draftSnap as QueryDocumentSnapshot<DocumentData>);
      }
      return null;
    } catch (error) {
      console.error('Error getting draft:', error);
      throw new Error('Failed to retrieve email draft');
    }
  }

  /**
   * Get all drafts for a user with optional limit
   */
  async getUserDrafts(userId: string, limitCount?: number): Promise<EmailDraft[]> {
    try {
      let q = query(
        collection(db, this.draftsCollection),
        where('userId', '==', userId),
        where('status', '==', 'draft'),
        orderBy('updatedAt', 'desc')
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(convertDocToEmailDraft);
    } catch (error) {
      console.error('Error getting user drafts:', error);
      throw new Error('Failed to retrieve email drafts');
    }
  }

  /**
   * Duplicate an existing draft
   */
  async duplicateDraft(draftId: string): Promise<string> {
    try {
      const originalDraft = await this.getDraftById(draftId);
      if (!originalDraft) {
        throw new Error('Original draft not found');
      }

      const now = Timestamp.now();
      const duplicatedDraft: any = {
        userId: originalDraft.userId,
        subject: `${originalDraft.subject} (Copy)`,
        htmlBody: originalDraft.htmlBody,
        textBody: originalDraft.textBody,
        to: [...originalDraft.to],
        cc: originalDraft.cc ? [...originalDraft.cc] : [],
        bcc: originalDraft.bcc ? [...originalDraft.bcc] : [],
        // Never share a Storage ref with the original (see attachmentsForDuplicate).
        attachments: attachmentsForDuplicate(originalDraft.attachments),
        senderAccountId: originalDraft.senderAccountId,
        isReply: originalDraft.isReply,
        isForward: originalDraft.isForward,
        createdAt: now,
        updatedAt: now,
        status: 'draft' as const,
        lastSavedAt: now,
        wordCount: originalDraft.wordCount,
      };
      // Firestore rejects undefined, so optional fields are added only when set.
      if (originalDraft.replyToEmailId !== undefined) duplicatedDraft.replyToEmailId = originalDraft.replyToEmailId;
      // The copy is a forward of the same message: keep what the owner removed from
      // it, or the copy would re-include (and send) those originals. linkedInvoiceId
      // is deliberately NOT copied - a duplicate must not also mark the invoice SENT.
      if (originalDraft.forwardExcludeNames !== undefined) {
        duplicatedDraft.forwardExcludeNames = [...originalDraft.forwardExcludeNames];
      }
      if (originalDraft.forwardFromGraphId) duplicatedDraft.forwardFromGraphId = originalDraft.forwardFromGraphId;

      const docRef = await addDoc(collection(db, this.draftsCollection), duplicatedDraft);
      return docRef.id;
    } catch (error) {
      console.error('Error duplicating draft:', error);
      throw new Error('Failed to duplicate email draft');
    }
  }

  /**
   * Search drafts by subject or content
   */
  async searchDrafts(userId: string, searchTerm: string, limitCount?: number): Promise<EmailDraft[]> {
    try {
      // For now, get all drafts and filter client-side
      // In a production app, you'd want to implement server-side search
      const allDrafts = await this.getUserDrafts(userId);

      const filteredDrafts = allDrafts.filter(draft => {
        const searchLower = searchTerm.toLowerCase();
        return (
          draft.subject.toLowerCase().includes(searchLower) ||
          draft.textBody.toLowerCase().includes(searchLower) ||
          draft.to.some(recipient => recipient.toLowerCase().includes(searchLower))
        );
      });

      return limitCount ? filteredDrafts.slice(0, limitCount) : filteredDrafts;
    } catch (error) {
      console.error('Error searching drafts:', error);
      throw new Error('Failed to search email drafts');
    }
  }

  /**
   * Set up real-time listener for user's drafts
   */
  subscribeToUserDrafts(userId: string, callback: (drafts: EmailDraft[]) => void): () => void {
    const q = query(
      collection(db, this.draftsCollection),
      where('userId', '==', userId),
      where('status', '==', 'draft'),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
      const drafts = querySnapshot.docs.map(convertDocToEmailDraft);
      callback(drafts);
    }, (error) => {
      console.error('Error in drafts subscription:', error);
    });

    return unsubscribe;
  }

  /**
   * Mark a draft as sent (useful for cleanup)
   */
  async markDraftAsSent(draftId: string): Promise<void> {
    try {
      const draftRef = doc(db, this.draftsCollection, draftId);
      await updateDoc(draftRef, {
        status: 'sent',
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error marking draft as sent:', error);
      throw new Error('Failed to update draft status');
    }
  }
}

// Export singleton instance
export const draftService = new DraftService();
export default draftService;
